/** The moves a backend state machine allows, keyed by the status they leave from. */
export type StatusTransitions<S extends string> = Readonly<Record<S, readonly S[]>>;

/** True when the machine can take an operation from `from` to `to` in one or more moves. */
function canReach<S extends string>(transitions: StatusTransitions<S>, from: S, to: S): boolean {
  const seen = new Set<S>();
  const queue = [...transitions[from]];
  while (queue.length) {
    const next = queue.shift() as S;
    if (next === to) return true;
    if (seen.has(next)) continue;
    seen.add(next);
    queue.push(...transitions[next]);
  }
  return false;
}

/**
 * The status to show after one more reading of a live operation. Readings arrive in any order — a `GET` sent
 * before the webhook can resolve after the socket already pushed the outcome — so one only wins when the
 * backend could have moved the operation there from what is on screen (steps in between may have been
 * missed). Anything else is an older snapshot and is dropped, and a final status is never replaced. A status
 * this build does not know is taken only while nothing is known yet.
 */
export function advanceStatus<S extends string>(
  transitions: StatusTransitions<S>,
  current: S | null,
  incoming: string | null | undefined,
): S | null {
  const isKnown = (status: string): status is S => Object.hasOwn(transitions, status);
  if (!incoming || incoming === current) return current;
  if (!isKnown(incoming)) return current ?? (incoming as S);
  if (!current || !isKnown(current)) return incoming;
  return canReach(transitions, current, incoming) ? incoming : current;
}
